import React, { useState, useMemo } from "react";
import { DollarSign, Coins } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";

// 2026 estimated bulk prices per kg of additive (Q1 2026, indicative)
// Source: aggregated supplier quotes & literature
const PRICES_USD = {
  base_eps:    1.8,         // EPS bead matrix
  bor:         3.2,         // Zn-borate / boric acid (TR origin)
  grafit:      4.5,         // Expandable graphite
  grafen:      85.0,        // GNP (industrial grade)
  hnt:         12.0,         // Halloysite nanotubes
};

const FX = {
  USD: { rate: 1.0,   symbol: "$",  flag: "🇺🇸" },
  EUR: { rate: 0.92,  symbol: "€",  flag: "🇪🇺" },
  TRY: { rate: 38.5,  symbol: "₺", flag: "🇹🇷" },
  GBP: { rate: 0.79,  symbol: "£",  flag: "🇬🇧" },
};

export default function PricingWidget({ mix }) {
  const { t } = useI18n();
  const [currency, setCurrency] = useState("USD");
  const fx = FX[currency];

  // Per kg final product cost: sum of base + additive prices weighted by mass fraction
  const breakdown = useMemo(() => {
    const baseFrac = Math.max(0, 1 - (mix.bor + mix.grafit + mix.grafen + mix.hnt) / 100);
    const items = [
      { name: "EPS Matrix", frac: baseFrac, price: PRICES_USD.base_eps, color: "#FFFFFF" },
      { name: "Boron",    frac: mix.bor / 100,    price: PRICES_USD.bor,    color: "#FFCC00" },
      { name: "Graphite", frac: mix.grafit / 100, price: PRICES_USD.grafit, color: "#00E5FF" },
      { name: "Graphene", frac: mix.grafen / 100, price: PRICES_USD.grafen, color: "#39FF14" },
      { name: "HNT",      frac: mix.hnt / 100,    price: PRICES_USD.hnt,    color: "#FF8C42" },
    ];
    items.forEach((i) => { i.cost_usd_per_kg = i.frac * i.price; });
    const total_usd = items.reduce((s, i) => s + i.cost_usd_per_kg, 0);
    return { items, total_usd };
  }, [mix]);

  const convert = (usd) => (usd * fx.rate).toFixed(2);

  return (
    <section className="pricing-widget border-2 border-[#FFCC00]" data-testid="pricing-widget">
      <header className="bg-[#FFCC00] text-[#0A0A0A] px-3 py-1.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Coins className="w-3.5 h-3.5"/>
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-bold">
            {t("pricing.header", "Estimated Cost · Multi-Currency")}
          </span>
        </div>
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger className="rounded-none border-[#0A0A0A] bg-[#FFCC00] text-[#0A0A0A] h-6 px-2 font-mono text-[10px] w-24"
            data-testid="currency-select">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent className="rounded-none bg-[#0A0A0A] text-white border-[#1F1F1F]">
            {Object.entries(FX).map(([code, f]) => (
              <SelectItem key={code} value={code} className="rounded-none font-mono text-xs">
                {f.flag} {code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </header>
      <div className="p-3 space-y-3">
        <div className="pricing-table p-3">
          {breakdown.items.map((it) => (
            <div key={it.name} className="pricing-row grid grid-cols-12 gap-2 items-center py-1.5 text-[11px] font-mono">
              <span className="pricing-name col-span-3 truncate" style={{ color: it.color }}>{it.name}</span>
              <span className="pricing-pct col-span-2 text-right">{(it.frac * 100).toFixed(2)}%</span>
              <span className="pricing-unit col-span-3 text-right">${it.price.toFixed(2)}/kg</span>
              <span className="pricing-val col-span-4 text-right" style={{ color: it.color, fontWeight: 700 }}>
                {fx.symbol}{convert(it.cost_usd_per_kg)}/kg
              </span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="pricing-summary-cell">
            <div className="pricing-summary-label tech-label">{t("pricing.summary.perKg", "TOTAL / KG")}</div>
            <div className="pricing-summary-val font-mono text-2xl" data-testid="price-per-kg">
              {fx.symbol}{convert(breakdown.total_usd)}
            </div>
          </div>
          <div className="pricing-summary-cell">
            <div className="pricing-summary-label tech-label">{t("pricing.summary.panel", "10 cm PANEL (m²)")}</div>
            <div className="pricing-summary-val font-mono text-2xl">
              {fx.symbol}{convert(breakdown.total_usd * 1.7)}
            </div>
          </div>
          <div className="pricing-summary-cell">
            <div className="pricing-summary-label tech-label">{t("pricing.summary.output", "100 m² OUTPUT")}</div>
            <div className="pricing-summary-val font-mono text-2xl">
              {fx.symbol}{convert(breakdown.total_usd * 170)}
            </div>
          </div>
        </div>

        <p
          className="font-mono text-[10px] leading-relaxed"
          style={{ color: "#A855F7" }}
          data-testid="pricing-note">
          {t(
            "pricing.note",
            "⚠ Indicative pricing — Q1 2026 spot supplier averages. Based on EPS 17 kg/m³ density and 10 cm panel thickness assumption (m²×thickness×density = 1.7 kg/m²)."
          )}
        </p>
      </div>
    </section>
  );
}
